import { Fragment, useMemo } from 'react';

import { HealthIcon, SignatureIcon, UserGroup03Icon } from '@hugeicons/core-free-icons';
import { CandidateCheckScreens } from 'constants/candidate_screens';
import {
  buildAllFormValues,
  buildNavPath as buildNavPathHelper,
  evaluateFieldVisibility,
} from 'helpers/candidate_application/form-page-helpers';
import { useQueryParams } from 'hooks/query-params';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { DataCard } from 'components/Cards/DataCards/DataCard';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import SliderMotion from 'components/Motion/SliderMotion';

import { selectCandidateApplication } from 'api/candidate-application-slice';

const DEPENDENT_CHECK_CONFIGS = [
  {
    type: 'us_medical_screen',
    screen: CandidateCheckScreens.US_MEDICAL_SCREEN,
    icon: HealthIcon,
    dependsOnSection: 'verify_identity',
  },
];

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [admin] = useQueryParams(['admin']);
  const { token } = useParams();

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);
  const {
    application: { candidate, brand, candidate_checks, forms, check_values },
    candidateFields,
    fetched,
  } = useSelector(selectCandidateApplication);

  // Create a lookup map for efficient check type lookups
  const checksByType = useMemo(() => {
    if (!candidate_checks) return {};
    return candidate_checks.reduce((acc, check) => {
      acc[check.type] = check;
      return acc;
    }, {});
  }, [candidate_checks]);

  const checkStatus = (check, checkValues = {}) => {
    const rejected =
      (checkValues?.rejected && checkValues?.rejected_values?.values.length > 0) || false;
    const fixed = (checkValues?.rejected && checkValues?.rejected_fixes?.values) || false;
    const completed = check?.completed || check?.completed_at || checkValues?.completed || false;
    const submitted = check?.submitted_at || checkValues?.submitted_at || false;

    let status = {
      text: t('statuses.yetToStart'),
      status: 'light',
    };

    if ((completed && !rejected) || submitted) {
      status.text = t('statuses.completed');
      status.status = 'success';
    }

    if (fixed) {
      status.text = t('statuses.fixed');
      status.status = 'success';
    }
    if (rejected && !fixed) {
      status.text = t('statuses.rejected');
      status.status = 'error';
    }

    return status;
  };

  // Prepare main check cards
  const referenceCheckCards = useMemo(() => {
    // Icon mapping to ensure fresh component references
    const iconMap = {
      reference: UserGroup03Icon,
      custom_reference: UserGroup03Icon,
    };

    const checkConfigs = [
      {
        type: 'reference',
        screen: CandidateCheckScreens.REFERENCE,
      },
      {
        type: 'custom_reference',
        screen: CandidateCheckScreens.CUSTOM_REFERENCE,
      },
    ];

    return (checkConfigs || [])
      ?.filter(config => checksByType?.[config.type])
      ?.map(config => {
        const check = checksByType?.[config?.type];
        return {
          check,
          title: t(`candidate.dashboard.${config?.type}.title`),
          description: t(`candidate.dashboard.${config?.type}.description`),
          icon: iconMap[config.type], // Use fresh icon reference from map
          disabled: !!check.submitted_at,
          status: checkStatus(check, {}),
          onClick: () =>
            navigate(buildNavPath(`/form_submission/candidate/${config?.screen}/${token}`)),
          code: config.type, // add code for DataCard key
        };
      });
  }, [checksByType, navigate, token, t]);

  // Prepare main check cards
  const checkCards = useMemo(() => {
    const iconMap = {
      document_signing: SignatureIcon,
    };

    const checkConfigs = [
      {
        type: 'document_signing',
        screen: CandidateCheckScreens.DOCUMENT_SIGNING,
      },
    ];

    return (checkConfigs || [])
      ?.filter(config => checksByType?.[config.type])
      ?.map(config => {
        const check = checksByType?.[config?.type];
        return {
          check,
          title: t(`candidate.dashboard.${config?.type}.title`),
          description: t(`candidate.dashboard.${config?.type}.description`),
          icon: iconMap[config.type], // Use fresh icon reference from map
          disabled: !!check.submitted_at,
          status: checkStatus(check, {}),
          onClick: () =>
            navigate(buildNavPath(`/form_submission/candidate/${config?.screen}/${token}`)),
          code: config.type, // add code for DataCard key
        };
      });
  }, [checksByType, navigate, token, t]);

  const dependentCheckCards = useMemo(() => {
    return DEPENDENT_CHECK_CONFIGS.filter(config => checksByType?.[config.type]).map(config => {
      const check = checksByType[config.type];
      const systemStatus = check_values?.system_values?.[`${config.type}_status`];
      const isCompleted = !!check.submitted_at || systemStatus === 'completed';
      return {
        check,
        title: t(`candidate.dashboard.${config.type}.title`),
        description: t(`candidate.dashboard.${config.type}.description`),
        icon: config.icon,
        dependsOnSection: config.dependsOnSection,
        disabled: isCompleted,
        status: checkStatus(check, { completed: isCompleted }),
        onClick: () =>
          navigate(buildNavPath(`/form_submission/candidate/${config.screen}/${token}`)),
        code: config.type,
      };
    });
  }, [checksByType, navigate, token, t, check_values]);

  const questionnaireSubChecks = useMemo(() => {
    if (!candidate_checks?.length) return [];
    const questionnaireChecks = (candidate_checks || [])?.filter(
      c => c.type === 'candidate_questionnaire',
    );
    const items = (questionnaireChecks || [])?.flatMap(questionnaireCheck =>
      (questionnaireCheck?.sub_checks || []).map(subCheck => {
        const dueDate = subCheck?.details?.due_date;
        return {
          check: subCheck,
          title: subCheck.title,
          description: t('candidate.dashboard.candidate_questionnaire.description'),
          message:
            subCheck?.details?.message && subCheck.details.use_message
              ? subCheck.details.message
              : null,
          icon: UserGroup03Icon,
          useExpirationDate: !!dueDate,
          expirationDate: dueDate,
          disabled: !!subCheck.completed_at,
          enableByDefault: true,
          checkValues: {},
          status: checkStatus(subCheck, {}),
          onClick: () => {
            const params = new URLSearchParams({ subCheckId: subCheck.id });
            if (dueDate) {
              params.set('expirationDate', dueDate);
            }
            navigate(
              buildNavPath(
                `/form_submission/candidate/${CandidateCheckScreens.QUESTIONNAIRE}/${token}?${params.toString()}`,
              ),
            );
          },
          code: `questionnaire-${questionnaireCheck.id}-${subCheck.id}`,
        };
      }),
    );
    return items.slice().sort((a, b) => {
      const dateA = a.expirationDate ? new Date(a.expirationDate).getTime() : Infinity;
      const dateB = b.expirationDate ? new Date(b.expirationDate).getTime() : Infinity;
      return dateA - dateB;
    });
  }, [candidate_checks, navigate, token, t]);

  // Helper function to check if a section is completed
  const isSectionCompleted = (sectionCode, status) => {
    // If status is an object (checkCards, questionnaireSubChecks), check the status property
    if (typeof status === 'object' && status !== null) {
      return status?.status === 'success' || status?.status === 'completed';
    }
    // If status is a string (candidateFieldsSections), check directly
    if (typeof status === 'string') {
      return status === 'completed';
    }
    // Fallback: check system_values
    const systemStatus = check_values?.system_values?.[`${sectionCode}_status`];
    return systemStatus === 'completed';
  };

  const visibleSubsectionsBySectionCode = useMemo(() => {
    if (!candidateFields) return {};

    const { fieldPathIndex } = candidateFields;
    const allValues = buildAllFormValues({
      candidateFields,
    });

    const isFieldVisible = field => evaluateFieldVisibility(field, allValues, fieldPathIndex);

    const getVisibleSubsections = subsections => {
      if (!Array.isArray(subsections) || subsections.length === 0) return [];

      return subsections.filter(subsection => {
        const fields = Array.isArray(subsection?.fields) ? subsection.fields : [];
        if (fields.length === 0) return false;

        return fields.some(isFieldVisible);
      });
    };

    // Build a map of section code to hasVisibleSubsections boolean
    const map = {};
    (candidateFields?.sections || []).forEach(section => {
      if (section?.subsections?.length > 0) {
        const visibleSubsections = getVisibleSubsections(section.subsections);
        map[section.code] = visibleSubsections.length > 0;
      }
    });

    return map;
  }, [candidateFields, check_values?.values]);

  const hasMatchingCode = section => {
    // Collect all codes from fields + subfields + altIdFields (same treatment)
    const allCodes = section?.subsections?.flatMap(sub =>
      sub?.fields?.flatMap(field => {
        const codes = [field?.code];
        if (Array.isArray(field?.subFields)) {
          codes.push(...field.subFields.map(sf => sf?.code));
        }
        const altIdGroups = field?.altIdFields || field?.alt_id_fields;
        if (Array.isArray(altIdGroups)) {
          altIdGroups.forEach(group => {
            (group?.subFields || group?.sub_fields || []).forEach(sf => codes.push(sf?.code));
          });
        }
        return codes;
      }),
    );

    // Check if any of the target codes exist in allCodes
    return (
      check_values?.rejected_values?.values?.some(code => allCodes.includes(code)) ||
      check_values?.rejected_values?.files?.some(code => allCodes.includes(code))
    );
  };

  const independentSteps = useMemo(() => {
    return [...referenceCheckCards, ...questionnaireSubChecks, ...checkCards];
  }, [referenceCheckCards, questionnaireSubChecks, checkCards]);

  // Combine all sections
  const applicationSteps = useMemo(() => {
    const { candidateFieldsSections, consentsSection } = (candidateFields?.sections || []).reduce(
      (acc, section) => {
        let containsRejectedCode = false;
        if (check_values?.rejected) {
          containsRejectedCode = hasMatchingCode(section);
        }
        const updateSection = {
          ...section,
          status:
            section.code === 'consents' && candidate.re_consent_requested_at
              ? 're_consent_required'
              : containsRejectedCode
                ? 'rejected'
                : check_values?.system_values?.[`${section.code}_status`],
          enableByDefault: section.code === 'personal_details' || false,
          hasVisibleSubsections:
            section?.subsections?.length > 0
              ? (visibleSubsectionsBySectionCode[section.code] ?? true)
              : undefined,
        };

        if (section.code === 'consents') {
          acc.consentsSection = updateSection;
        } else {
          acc.candidateFieldsSections.push(updateSection);
        }
        return acc;
      },
      { candidateFieldsSections: [], consentsSection: null },
    );

    //Remove sections with hasVisibleSubsections === false
    const filteredCandidateFieldSections = (candidateFieldsSections || [])?.filter(
      s => s.hasVisibleSubsections !== false,
    );

    // Build the full ordered list of sections
    const fullSectionList = [
      ...filteredCandidateFieldSections,
      ...(consentsSection ? [consentsSection] : []),
    ];

    // Enable sections sequentially - each section is enabled only if all previous sections are completed
    const isAdmin = admin === 'true' || admin === true;

    const sectionsWithDisabledState = fullSectionList.map((section, index) => {
      if (section.code === 'consents') {
        const allSections = [...fullSectionList, ...independentSteps];
        const otherSections = allSections.filter(s => s.code !== 'consents');
        const allOtherSectionsCompleted = otherSections.every(otherSection =>
          isSectionCompleted(otherSection.code, otherSection.status),
        );

        const disabled =
          !allOtherSectionsCompleted ||
          (candidate.submitted_at &&
            !check_values.rejected &&
            !candidate.re_consent_requested_at) ||
          (check_values?.system_values?.[`${section.code}_status`] === 'completed' &&
            !candidate.re_consent_requested_at) ||
          isAdmin;

        return {
          ...section,
          disabled,
          status: disabled ? 'locked' : section.status,
        };
      }

      // First section is always enabled
      if (index === 0 || section.status === 'rejected' || candidate?.submitted_at) {
        return {
          ...section,
          disabled: section.disabled,
        };
      }

      if (section.enableByDefault) {
        return {
          ...section,
          disabled: section.disabled,
        };
      }

      // Check if all previous sections are completed
      const previousSections = fullSectionList.slice(0, index);
      const allPreviousCompleted = previousSections.every(prevSection =>
        isSectionCompleted(prevSection.code, prevSection.status),
      );

      const disabled = !allPreviousCompleted;
      return {
        ...section,
        disabled,
        status: disabled ? 'locked' : section.status,
      };
    });

    const result = [];
    for (const section of sectionsWithDisabledState) {
      result.push(section);
      const deps = dependentCheckCards.filter(dc => dc.dependsOnSection === section.code);
      for (const dep of deps) {
        const isDependencyLocked =
          !isSectionCompleted(section.code, section.status) && !dep.check.submitted_at;
        result.push({
          ...dep,
          disabled: isDependencyLocked || !!dep.check.submitted_at,
          status: isDependencyLocked ? 'locked' : dep.status,
        });
      }
    }
    return result;
  }, [
    independentSteps,
    dependentCheckCards,
    candidateFields?.sections,
    check_values,
    candidate,
    admin,
    visibleSubsectionsBySectionCode,
  ]);

  const allApplicationSteps = useMemo(() => {
    return [...independentSteps, ...applicationSteps];
  }, [independentSteps, applicationSteps]);

  const getStatus = status => {
    switch (status) {
      case 'success':
      case 'completed':
        return { text: t('statuses.completed'), status: 'success' };
      case 'in-progress':
        return { text: t('statuses.in-progress'), status: 'in-progress' };
      case 'rejected':
        return { text: t('statuses.requiresUpdate'), status: 'warning' };
      case 'locked':
        return {
          text: t('candidate.dashboard.locked', { defaultValue: 'Locked' }),
          status: 'neutral',
        };
      case 're_consent_required':
        return { text: t('statuses.reConsentRequired'), status: 'warning' };
      case 'error':
        return { text: t('statuses.error'), status: 'error' };
      default:
        return { text: t('statuses.yetToStart'), status: 'neutral' };
    }
  };

  // Early return if data not fetched yet
  if (!fetched) {
    return (
      <CandidatePage token={token} withTopLogo brand={brand}>
        <CandidatePage.View>
          <CandidatePage.Card>
            <p>{t('common.loading')}</p>
          </CandidatePage.Card>
        </CandidatePage.View>
      </CandidatePage>
    );
  }

  const navigateToSection = section => {
    if (section.disabled) return;
    if (section.index_page) {
      navigate(buildNavPath(`/form_submission/candidate/${section.code}/${token}`));
    } else {
      navigate(buildNavPath(`/form_submission/candidate/${section.code}/${token}/page/0`));
    }
  };

  return (
    <CandidatePage token={token} brand={brand} withTopLogo displayLanguageSelector>
      <CandidatePage.View>
        <CandidatePage.Card small>
          <CandidatePage.Header
            brand={brand}
            showLogo={false}
            candidateName={candidate?.first_name}
            showUserDetails
            userDetails={{
              name: candidate?.first_name + ' ' + candidate?.last_name,
              email: candidate?.email,
              totalSections: (allApplicationSteps || []).length,
              completedSections: (allApplicationSteps || [])?.filter(
                section => section?.status?.status === 'success' || section?.status === 'completed',
              ).length,
            }}
          />
          <div className="my-16" />
          <AnimatePresence mode="wait">
            <div className="d-flex flex-column gap-16">
              {check_values?.rejected && (
                <FormMessage
                  message={
                    check_values?.rejected_values?.reason || t('candidate.dashboard.verifyDetails')
                  }
                  type={MessageType.Warning}
                  closeable={false}
                  noMargin
                />
              )}

              {allApplicationSteps?.length === 0 && (
                <SliderMotion key="success-application" duration={0.6} delay={0.15}>
                  <div className="d-flex flex-column u-gap-large align-items-center">
                    <img
                      src="assets/images/success-application.jpg"
                      alt="Application submitted illustration"
                      className="u-width-75 u-border-radius--half"
                    />
                    <FormMessage
                      type="Info"
                      closeable={false}
                      message={t('candidate.dashboard.candidateFormCompleted')}
                    />
                  </div>
                </SliderMotion>
              )}
              {independentSteps?.length > 0 && (
                <div className="d-flex flex-column gap-4">
                  <div className="flex justify-between gap-2 mb-4">
                    <h2 className="text-2xl font-medium text-neutral-6 uppercase">
                      Independent sections
                    </h2>
                    <span className="text-xl font-regular text-text-tertiary">
                      Complete in any order
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-8">
                    {(independentSteps || [])?.map((section, index) => {
                      if (
                        section?.subsections?.length > 0 &&
                        section?.hasVisibleSubsections === false
                      ) {
                        return null;
                      }
                      return (
                        <SliderMotion
                          key={section.code + index}
                          duration={0.6}
                          delay={index * 0.15}>
                          <DataCard
                            key={section.code} // code should be unique
                            keyProp={section.code}
                            title={section.title || section.code}
                            useExpirationDate={section?.useExpirationDate}
                            expirationDate={section?.expirationDate}
                            description={section.description}
                            message={section?.message}
                            background="secondary"
                            disabled={section?.disabled}
                            status={
                              typeof section?.status === 'string'
                                ? getStatus(section?.status)
                                : section?.status
                            }
                            showStatusBorder
                            onClick={() =>
                              section?.onClick ? section.onClick() : navigateToSection(section)
                            }
                          />
                        </SliderMotion>
                      );
                    })}
                  </div>
                </div>
              )}

              {applicationSteps?.length > 0 &&
                (() => {
                  const steps = applicationSteps || [];
                  const firstDisabledIndex = steps.findIndex(s => s.disabled);
                  const showBanner =
                    firstDisabledIndex > 0 &&
                    steps[firstDisabledIndex - 1] &&
                    steps[firstDisabledIndex];
                  const nextDisabledSection = steps[firstDisabledIndex];
                  const bannerMessage = showBanner
                    ? nextDisabledSection?.code === 'consents'
                      ? admin === 'true' || admin === true
                        ? t('candidate.dashboard.adminNoAccessToConsents', {
                            defaultValue: 'Support has no access to the consents section',
                          })
                        : t('candidate.dashboard.completeAllStepsToProceed', {
                            defaultValue:
                              'Complete all sections including independent sections to proceed',
                          })
                      : t('candidate.dashboard.completeSectionToContinue', {
                          section:
                            steps[firstDisabledIndex - 1].title ||
                            steps[firstDisabledIndex - 1].code,
                          defaultValue: `Complete "${steps[firstDisabledIndex - 1].title || steps[firstDisabledIndex - 1].code}" to continue`,
                        })
                    : null;

                  return (
                    <div className="d-flex flex-column gap-4">
                      <div className="flex justify-between gap-2 mb-4">
                        <h2 className="text-2xl font-medium text-neutral-6 uppercase">
                          Application sections
                        </h2>
                        <span className="text-xl font-regular text-text-tertiary">
                          Complete in order
                        </span>
                      </div>
                      <div className="d-flex flex-column gap-8">
                        {steps.map((section, index) => {
                          if (
                            section?.subsections?.length > 0 &&
                            section?.hasVisibleSubsections === false
                          ) {
                            return null;
                          }
                          return (
                            <Fragment key={section.code + index}>
                              {index === firstDisabledIndex && bannerMessage && (
                                <FormMessage
                                  message={bannerMessage}
                                  type={MessageType.Info}
                                  closeable={false}
                                  noMargin
                                  small
                                />
                              )}
                              <SliderMotion duration={0.6} delay={index * 0.15}>
                                <DataCard
                                  key={section.code}
                                  keyProp={section.code}
                                  title={section.title || section.code}
                                  useExpirationDate={section?.useExpirationDate}
                                  expirationDate={section?.expirationDate}
                                  description={section.description}
                                  message={section?.message}
                                  background="secondary"
                                  disabled={section?.disabled}
                                  status={
                                    typeof section?.status === 'string'
                                      ? getStatus(section?.status)
                                      : section?.status
                                  }
                                  showStatusBorder
                                  onClick={() =>
                                    section?.onClick
                                      ? section.onClick()
                                      : navigateToSection(section)
                                  }
                                />
                              </SliderMotion>
                            </Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
            </div>
          </AnimatePresence>
        </CandidatePage.Card>
      </CandidatePage.View>
    </CandidatePage>
  );
};

export default CandidateDashboard;
